import React, { useState } from 'react';
import { Box, Paper, Typography, Stack, Button, TextField, CircularProgress } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import AssessmentIcon from '@mui/icons-material/Assessment';
import {
  exportRides, exportPayments, exportWalletTransactions, exportAuditLogs, ExportParams,
} from '../services/exportService';
import { useToast } from '../lib/useToast';
import { getApiErrorMessage } from '../lib/toast';
import { useCan } from '../auth/Can';
import { Permission } from '../types/Auth';
import PageHelp from '../components/common/PageHelp';

interface ReportDef {
  key: string;
  title: string;
  description: string;
  permission: Permission;
  run: (p: ExportParams) => Promise<void>;
}

const REPORTS: ReportDef[] = [
  { key: 'rides', title: 'Rides', description: 'All rides with fare, status, cities and timestamps.', permission: 'rides:read', run: exportRides },
  { key: 'payments', title: 'Payments', description: 'Per-ride payment records.', permission: 'payments:read', run: exportPayments },
  { key: 'wallet', title: 'Wallet Transactions', description: 'Credits, debits, earnings, refunds and adjustments.', permission: 'wallet:read', run: exportWalletTransactions },
  { key: 'audit', title: 'Audit Log', description: 'Every admin action with actor, entity and result.', permission: 'audit:read', run: exportAuditLogs },
];

const ReportCenter: React.FC = () => {
  const toast = useToast();
  const { can } = useCan();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const visible = REPORTS.filter((r) => can(r.permission));

  const handleExport = async (r: ReportDef) => {
    setBusy(r.key);
    try {
      await r.run({ from: from || undefined, to: to || undefined });
      toast.success(`${r.title} export downloaded`);
    } catch (e) {
      toast.error(getApiErrorMessage(e, `Failed to export ${r.title}`));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <AssessmentIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Report Center</Typography>
        <PageHelp k="reports" />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Export datasets to CSV. Date filters are optional and apply to each report's primary timestamp.
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField label="From" type="date" size="small" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextField label="To" type="date" size="small" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
          <Typography variant="caption" color="text.secondary">Leave blank to export everything (capped at 50k rows).</Typography>
        </Stack>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {visible.map((r) => (
          <Paper key={r.key} sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700 }}>{r.title}</Typography>
              <Typography variant="body2" color="text.secondary">{r.description}</Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={busy === r.key ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              disabled={busy != null}
              onClick={() => handleExport(r)}
            >
              CSV
            </Button>
          </Paper>
        ))}
        {visible.length === 0 && (
          <Typography color="text.secondary">You don't have permission to export any datasets.</Typography>
        )}
      </Box>
    </Box>
  );
};

export default ReportCenter;
